import PropTypes from 'prop-types';
import React from 'react';
import { Box, Button, Chip, Divider, Paper, Typography } from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import { isEmpty } from 'lodash';

import colors from 'common/constants/colors';
import {
  PENDING_CHANGES_HELPER_TEXT,
  SigDraftAction,
  SigDraftActionLabel,
} from 'enums/SigManagement.enum';

import AssociationStatusChip from './AssociationStatusChip';

const actionChipColour = {
  [SigDraftAction.ADD]: 'info',
  [SigDraftAction.SET_INACTIVE]: 'warning',
  [SigDraftAction.SET_ACTIVE]: 'success',
  [SigDraftAction.EDIT_SIG]: 'info',
};

/**
 * Everything the commit is about to write, in one place.
 *
 * The cards above show each association's staged state in situ, which is right for working but
 * wrong for deciding: the changes are spread across two lists behind two filters, and one made
 * three clicks ago may not even be on screen. This is the block to read before committing, and the
 * only place every pending change is guaranteed visible.
 *
 * Each row says the same three things in the same order — what will happen, what it happens to, and
 * what changes — so a set of them can be scanned rather than read.
 */
const PendingSigChangesCard = ({ changes, onUndo }) => {
  const changeCount = changes.length;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${isEmpty(changes) ? colors.cardBorderGrey : colors.pendingChangesBlueBorder}`,
        bgcolor: isEmpty(changes) ? colors.white : colors.pendingChangesBlue,
        px: 2.5,
        py: 2,
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
        <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
          Pending changes
        </Typography>
        {changeCount > 0 && (
          <Chip
            size='small'
            color='primary'
            label={`${changeCount} pending change${changeCount === 1 ? '' : 's'}`}
          />
        )}
      </Box>

      <Typography variant='caption' color='text.secondary'>
        {PENDING_CHANGES_HELPER_TEXT}
      </Typography>

      {isEmpty(changes) ? (
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1.5 }}>
          No pending changes. Add or update a direction or a catalog item to see it here.
        </Typography>
      ) : (
        <Box sx={{ mt: 1.5 }}>
          {changes.map(
            ({ key, action, item, label, detail, fromText, undo, ...transition }, index) => (
              <React.Fragment key={key}>
                {index > 0 && <Divider sx={{ my: 1.5 }} />}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                  }}
                >
                  <Chip
                    size='small'
                    variant='outlined'
                    color={actionChipColour[action]}
                    label={SigDraftActionLabel[action]}
                    sx={{ minWidth: 108, fontWeight: 500, bgcolor: colors.white }}
                  />

                  <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
                    <Typography variant='caption' color='text.secondary'>
                      {item}
                    </Typography>
                    <Typography variant='body2' sx={{ wordBreak: 'break-word' }}>
                      {label}
                    </Typography>
                    {detail && (
                      <Typography variant='caption' color='text.secondary'>
                        {detail}
                      </Typography>
                    )}

                    {/* A status flip shows both sides as chips; a text edit shows the wording it
                      replaces. Either way the row says what it is changing FROM, so nobody has to
                      remember. */}
                    {!isEmpty(fromText) && (
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start', mt: 0.5 }}>
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          sx={{ textDecoration: 'line-through', wordBreak: 'break-word' }}
                        >
                          {fromText}
                        </Typography>
                      </Box>
                    )}

                    {'toIsActive' in transition && (
                      <Box sx={{ mt: 0.75 }}>
                        <AssociationStatusChip
                          isActive={transition.toIsActive}
                          serverIsActive={transition.fromIsActive}
                          isStaged
                        />
                      </Box>
                    )}
                  </Box>

                  <Button
                    size='small'
                    color='inherit'
                    startIcon={<UndoIcon />}
                    onClick={() => onUndo(undo)}
                    aria-label={`Undo ${SigDraftActionLabel[action]}: ${label}`}
                  >
                    Undo
                  </Button>
                </Box>
              </React.Fragment>
            )
          )}
        </Box>
      )}
    </Paper>
  );
};

export default PendingSigChangesCard;

PendingSigChangesCard.propTypes = {
  changes: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      action: PropTypes.oneOf(Object.values(SigDraftAction)).isRequired,
      item: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      detail: PropTypes.string,
      fromText: PropTypes.string,
      fromIsActive: PropTypes.bool,
      toIsActive: PropTypes.bool,
      undo: PropTypes.shape({ kind: PropTypes.string.isRequired }).isRequired,
    })
  ),
  onUndo: PropTypes.func.isRequired,
};

PendingSigChangesCard.defaultProps = {
  changes: [],
};
